import { Station, MetroLine } from '../types';

// Helper function to create a station
function createStation(
  id: string,
  name: string,
  x: number,
  y: number,
  connections: string[] = [],
  lines: string[] = [],
  isInterchange: boolean = false,
  zone: number = 1
): Station {
  return {
    id,
    name,
    position: { x, y },
    connections,
    lines,
    isInterchange,
    zone
  };
}

// Create a more complex metro system resembling London's tube network
export const stations: Station[] = [
  // Central Line (Red) - East-West
  createStation('liverpool-street', 'Liverpool Street', 820, 340, [], ['central', 'metropolitan', 'circle'], true, 1),
  createStation('bank', 'Bank', 760, 380, [], ['central', 'northern', 'waterloo'], true, 1),
  createStation('st-pauls', 'St. Paul\'s', 700, 360, [], ['central'], false, 1),
  createStation('chancery-lane', 'Chancery Lane', 650, 340, [], ['central'], false, 1),
  createStation('holborn', 'Holborn', 600, 320, [], ['central', 'piccadilly'], true, 1),
  createStation('tottenham-court', 'Tottenham Court Road', 560, 300, [], ['central', 'northern'], true, 1),
  createStation('oxford-circus', 'Oxford Circus', 520, 280, [], ['central', 'victoria', 'bakerloo'], true, 1),
  createStation('bond-street', 'Bond Street', 480, 260, [], ['central', 'jubilee'], true, 1),
  createStation('marble-arch', 'Marble Arch', 440, 240, [], ['central'], false, 1),
  createStation('lancaster-gate', 'Lancaster Gate', 400, 220, [], ['central'], false, 1),
  createStation('queensway', 'Queensway', 360, 220, [], ['central'], false, 2),
  createStation('notting-hill', 'Notting Hill Gate', 320, 220, [], ['central', 'circle', 'district'], true, 2),
  
  // Northern Line (Black) - North-South
  createStation('camden-town', 'Camden Town', 560, 180, [], ['northern'], false, 2),
  createStation('euston', 'Euston', 580, 220, [], ['northern', 'victoria'], true, 1),
  createStation('warren-street', 'Warren Street', 560, 240, [], ['northern', 'victoria'], true, 1),
  createStation('goodge-street', 'Goodge Street', 560, 270, [], ['northern'], false, 1),
  // tottenham-court already defined
  createStation('leicester-square', 'Leicester Square', 580, 330, [], ['northern', 'piccadilly'], true, 1),
  createStation('charing-cross', 'Charing Cross', 600, 370, [], ['northern', 'bakerloo'], true, 1),
  createStation('embankment', 'Embankment', 620, 400, [], ['northern', 'bakerloo', 'circle', 'district'], true, 1),
  createStation('waterloo', 'Waterloo', 640, 440, [], ['northern', 'jubilee', 'waterloo'], true, 1),
  
  // Victoria Line (Light Blue) - Northeast-Southwest
  createStation('kings-cross', 'King\'s Cross St. Pancras', 640, 220, [], ['victoria', 'piccadilly', 'metropolitan', 'circle'], true, 1),
  // euston already defined
  // warren-street already defined
  // oxford-circus already defined
  createStation('green-park', 'Green Park', 520, 320, [], ['victoria', 'jubilee', 'piccadilly'], true, 1),
  createStation('victoria', 'Victoria', 520, 400, [], ['victoria', 'circle', 'district'], true, 1),
  
  // Piccadilly Line (Dark Blue) - East-West with Northern branch
  // kings-cross already defined
  createStation('russell-square', 'Russell Square', 620, 280, [], ['piccadilly'], false, 1),
  // holborn already defined
  createStation('covent-garden', 'Covent Garden', 580, 310, [], ['piccadilly'], false, 1),
  // leicester-square already defined
  createStation('piccadilly-circus', 'Piccadilly Circus', 540, 320, [], ['piccadilly', 'bakerloo'], true, 1),
  // green-park already defined
  createStation('hyde-park-corner', 'Hyde Park Corner', 480, 320, [], ['piccadilly'], false, 1),
  createStation('knightsbridge', 'Knightsbridge', 440, 320, [], ['piccadilly'], false, 1),
  createStation('south-kensington', 'South Kensington', 400, 340, [], ['piccadilly', 'circle', 'district'], true, 1),
  createStation('gloucester-road', 'Gloucester Road', 360, 340, [], ['piccadilly', 'circle', 'district'], true, 1),
  
  // Jubilee Line (Silver/Gray) - Northwest-Southeast
  createStation('baker-street', 'Baker Street', 480, 220, [], ['jubilee', 'bakerloo', 'metropolitan', 'circle'], true, 1),
  // bond-street already defined
  // green-park already defined
  createStation('westminster', 'Westminster', 580, 400, [], ['jubilee', 'circle', 'district'], true, 1),
  // waterloo already defined
  createStation('london-bridge', 'London Bridge', 720, 420, [], ['jubilee', 'northern'], true, 1),
  
  // Metropolitan Line (Purple) - Northwest-East
  // baker-street already defined
  createStation('great-portland', 'Great Portland Street', 520, 200, [], ['metropolitan', 'circle'], false, 1),
  createStation('euston-square', 'Euston Square', 580, 200, [], ['metropolitan', 'circle'], false, 1),
  // kings-cross already defined
  createStation('farringdon', 'Farringdon', 680, 280, [], ['metropolitan', 'circle'], false, 1),
  createStation('barbican', 'Barbican', 720, 300, [], ['metropolitan', 'circle'], false, 1),
  createStation('moorgate', 'Moorgate', 760, 320, [], ['metropolitan', 'circle', 'northern'], true, 1),
  // liverpool-street already defined
  createStation('aldgate', 'Aldgate', 800, 380, [], ['metropolitan', 'circle'], false, 1),
  
  // Circle/District Lines (Yellow/Green) - Loop + Extensions
  // notting-hill already defined
  createStation('high-street-kensington', 'High Street Kensington', 340, 280, [], ['circle', 'district'], false, 1),
  // gloucester-road already defined
  // south-kensington already defined
  createStation('sloane-square', 'Sloane Square', 460, 360, [], ['circle', 'district'], false, 1),
  // victoria already defined
  // st-james-park already defined
  createStation('st-james-park', 'St. James\'s Park', 540, 380, [], ['circle', 'district'], false, 1),
  // westminster already defined
  // embankment already defined
  createStation('temple', 'Temple', 650, 380, [], ['circle', 'district'], false, 1),
  createStation('blackfriars', 'Blackfriars', 680, 400, [], ['circle', 'district'], false, 1),
  createStation('mansion-house', 'Mansion House', 720, 400, [], ['circle', 'district'], false, 1),
  createStation('cannon-street', 'Cannon Street', 740, 410, [], ['circle', 'district'], false, 1),
  createStation('monument', 'Monument', 760, 400, [], ['circle', 'district', 'northern'], true, 1),
  // tower-hill already defined
  createStation('tower-hill', 'Tower Hill', 780, 420, [], ['circle', 'district'], false, 1),
  // aldgate already defined
  
  // Bakerloo Line (Brown) - Northwest-Southeast
  createStation('paddington', 'Paddington', 360, 200, [], ['bakerloo', 'circle'], true, 1),
  createStation('edgware-road', 'Edgware Road', 400, 200, [], ['bakerloo', 'circle'], false, 1),
  createStation('marylebone', 'Marylebone', 440, 200, [], ['bakerloo'], false, 1),
  // baker-street already defined
  createStation('regents-park', 'Regent\'s Park', 500, 240, [], ['bakerloo'], false, 1),
  // oxford-circus already defined
  // piccadilly-circus already defined
  // charing-cross already defined
  // embankment already defined
  
  // Waterloo & City Line (Light Green) - Short connector
  // bank already defined
  // waterloo already defined
  
  // Additional interchanges and connections
  createStation('stratford', 'Stratford', 900, 280, [], ['central', 'jubilee'], true, 3),
  createStation('canada-water', 'Canada Water', 800, 460, [], ['jubilee'], false, 2),
  createStation('canary-wharf', 'Canary Wharf', 880, 500, [], ['jubilee'], false, 2),
];

// Define metro lines
export const metroLines: MetroLine[] = [
  {
    id: 'central',
    name: 'Central Line',
    color: '#E32017', // Red
    stations: [
      'notting-hill', 'queensway', 'lancaster-gate', 'marble-arch', 'bond-street',
      'oxford-circus', 'tottenham-court', 'holborn', 'chancery-lane', 'st-pauls',
      'bank', 'liverpool-street', 'stratford'
    ],
    trainCount: 8,
    frequency: 120000 // 2 minutes between trains
  },
  {
    id: 'northern',
    name: 'Northern Line',
    color: '#000000', // Black
    stations: [
      'camden-town', 'euston', 'warren-street', 'goodge-street', 'tottenham-court', 
      'leicester-square', 'charing-cross', 'embankment', 'waterloo', 'london-bridge',
      'moorgate', 'bank', 'monument'
    ],
    trainCount: 7,
    frequency: 150000 // 2.5 minutes between trains
  },
  {
    id: 'victoria',
    name: 'Victoria Line',
    color: '#0098D4', // Light Blue
    stations: [
      'kings-cross', 'euston', 'warren-street', 'oxford-circus', 'green-park', 'victoria'
    ],
    trainCount: 5,
    frequency: 100000 // 1.67 minutes between trains
  },
  {
    id: 'piccadilly',
    name: 'Piccadilly Line',
    color: '#003688', // Dark Blue
    stations: [
      'kings-cross', 'russell-square', 'holborn', 'covent-garden', 'leicester-square',
      'piccadilly-circus', 'green-park', 'hyde-park-corner', 'knightsbridge', 
      'south-kensington', 'gloucester-road'
    ],
    trainCount: 6,
    frequency: 180000 // 3 minutes between trains
  },
  {
    id: 'jubilee',
    name: 'Jubilee Line',
    color: '#A0A5A9', // Silver/Gray
    stations: [
      'baker-street', 'bond-street', 'green-park', 'westminster', 'waterloo',
      'london-bridge', 'canada-water', 'canary-wharf', 'stratford'
    ],
    trainCount: 6,
    frequency: 160000 // 2.67 minutes between trains
  },
  {
    id: 'metropolitan',
    name: 'Metropolitan Line',
    color: '#9B0056', // Purple
    stations: [
      'baker-street', 'great-portland', 'euston-square', 'kings-cross', 'farringdon',
      'barbican', 'moorgate', 'liverpool-street', 'aldgate'
    ],
    trainCount: 5,
    frequency: 200000 // 3.33 minutes between trains
  },
  {
    id: 'circle',
    name: 'Circle Line',
    color: '#FFD300', // Yellow
    stations: [
      'paddington', 'edgware-road', 'baker-street', 'great-portland', 'euston-square',
      'kings-cross', 'farringdon', 'barbican', 'moorgate', 'liverpool-street', 'aldgate',
      'tower-hill', 'monument', 'cannon-street', 'mansion-house', 'blackfriars', 'temple', 
      'embankment', 'westminster', 'st-james-park', 'victoria', 'sloane-square',
      'south-kensington', 'gloucester-road', 'high-street-kensington', 'notting-hill'
    ],
    trainCount: 6,
    frequency: 300000 // 5 minutes between trains
  },
  {
    id: 'district',
    name: 'District Line',
    color: '#00782A', // Green
    stations: [
      'notting-hill', 'high-street-kensington', 'gloucester-road', 'south-kensington',
      'sloane-square', 'victoria', 'st-james-park', 'westminster', 'embankment',
      'temple', 'blackfriars', 'mansion-house', 'cannon-street', 'monument', 'tower-hill'
    ],
    trainCount: 7,
    frequency: 180000 // 3 minutes between trains
  },
  {
    id: 'bakerloo',
    name: 'Bakerloo Line',
    color: '#B36305', // Brown
    stations: [
      'paddington', 'edgware-road', 'marylebone', 'baker-street', 'regents-park',
      'oxford-circus', 'piccadilly-circus', 'charing-cross', 'embankment'
    ],
    trainCount: 5,
    frequency: 210000 // 3.5 minutes between trains
  },
  {
    id: 'waterloo',
    name: 'Waterloo & City Line',
    color: '#95CDBA', // Light Green
    stations: ['bank', 'waterloo'],
    trainCount: 2,
    frequency: 300000 // 5 minutes between trains
  }
];

// Function to properly connect stations based on metro lines
export function setupConnections(stations: Station[], lines: MetroLine[]): Station[] {
  // Clear all connections first
  const updatedStations = stations.map(station => ({
    ...station,
    connections: []
  }));
  
  // Create a map for easy lookup
  const stationMap = new Map<string, Station>();
  updatedStations.forEach(station => {
    stationMap.set(station.id, station);
  });
  
  // Process each line
  lines.forEach(line => {
    // Connect sequential stations on each line
    for (let i = 0; i < line.stations.length - 1; i++) {
      const currentId = line.stations[i];
      const nextId = line.stations[i + 1];
      
      const current = stationMap.get(currentId);
      const next = stationMap.get(nextId);
      
      if (current && next) {
        // Add connection if not already present
        if (!current.connections.includes(nextId)) {
          current.connections.push(nextId);
        }
        
        // Add reverse connection
        if (!next.connections.includes(currentId)) {
          next.connections.push(currentId);
        }
      }
    }
  });
  
  // Update the lines for each station
  updatedStations.forEach(station => {
    station.lines = lines
      .filter(line => line.stations.includes(station.id))
      .map(line => line.id);
  });
  
  return updatedStations;
}

// Export the connected stations
export const connectedStations = setupConnections(stations, metroLines); 